import { FC, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { listGiftBrandSellerDeliveries } from '@shared/api/giftDelivery/giftDelivery.controller';
import { GiftDeliveryStatus } from '@shared/api/giftDelivery/giftDelivery.interface';
import { giftItemBrandListDataAtom } from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import { SettlementInfo } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { useAsyncFn } from 'react-use';

import { formatDate, getItemName } from '../../giftSeller/utils/delivery.utils';
import {
  computeSettlementLines,
  formatWon,
  getSettlementBrandId,
} from '../../giftSeller/utils/settlement.utils';

// 업체(giftItemBrand)가 없는 배송건 필터용 키
const NO_BRAND = '__none__';

const toYmd = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// 정산관리: 배송완료 건 기준 정산 (건별 정산금 + 총합). 정산 확정 API는 백엔드 개발 중.
export const GiftBrandSettlementPage: FC = function GiftBrandSettlementPage() {
  // Admin/GiftAgent는 전 브랜드(admin), 브랜드셀러는 본인 브랜드(gift-brand-seller)로 조회
  const { giftBrandRole } = useAuth();
  const role = giftBrandRole() as LowerRole.Admin | LowerRole.GiftBrandSeller;
  const isAdminView = role === LowerRole.Admin;

  // 공급사 정산정보(settlementInfo) 조회 — 브랜드 id로 매핑해 정산금 계산에 사용
  useGiftItemBrandListHook(role);
  const brands = useRecoilValue(giftItemBrandListDataAtom);
  const settlementByBrand = useMemo(() => {
    const map = new Map<string, SettlementInfo | undefined>();
    (brands ?? []).forEach((b) => map.set(b._id, b.settlementInfo));
    return (brandId?: string) => (brandId ? map.get(brandId) : undefined);
  }, [brands]);

  const [{ loading, value: delivered }, fetchDelivered] = useAsyncFn(
    async () =>
      listGiftBrandSellerDeliveries(
        { deliveryStatus: GiftDeliveryStatus.Delivered },
        { 'shipping.deliveredAt': -1 },
        role,
      ),
    [role],
  );

  useEffect(() => {
    fetchDelivered();
  }, [fetchDelivered]);

  const rows = useMemo(() => delivered ?? [], [delivered]);
  // 택배비 묶음(송장번호) 판정은 전체 건 기준이어야 하므로 필터 전에 계산
  const allLines = useMemo(
    () => computeSettlementLines(rows, settlementByBrand),
    [rows, settlementByBrand],
  );

  // 정산 받는 업체(공급사) 목록 — 배송건에 등장하는 giftItemBrand 기준.
  // 업체가 없는 건은 NO_BRAND('빈업체') 키로 묶어 함께 필터 가능하게 한다.
  const brandOptions = useMemo(() => {
    const map = new Map<string, string>();
    let hasNoBrand = false;
    rows.forEach((d) => {
      const id = getSettlementBrandId(d);
      if (id) {
        const name = typeof d.giftItemBrand === 'object' ? d.giftItemBrand?.name : '';
        map.set(id, name || id);
      } else {
        hasNoBrand = true;
      }
    });
    const options = Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    // 빈업체는 항상 마지막에
    if (hasNoBrand) options.push({ id: NO_BRAND, name: '빈업체' });
    return options;
  }, [rows]);

  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  // 선택 업체가 목록에서 사라지면(데이터 변경) 전체로 복귀
  useEffect(() => {
    if (selectedBrand !== 'all' && !brandOptions.some((b) => b.id === selectedBrand)) {
      setSelectedBrand('all');
    }
  }, [brandOptions, selectedBrand]);

  // 기간 필터 (배송완료일 기준, YYYY-MM-DD)
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  const lines = useMemo(() => {
    const fromT = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toT = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : null;
    return allLines.filter((l) => {
      // 업체
      if (
        selectedBrand !== 'all' &&
        (getSettlementBrandId(l.delivery) || NO_BRAND) !== selectedBrand
      ) {
        return false;
      }
      // 기간 (배송완료일이 범위에 포함된 건만)
      if (fromT !== null || toT !== null) {
        const at = l.delivery.shipping?.deliveredAt;
        if (!at) return false;
        const t = new Date(at).getTime();
        if (fromT !== null && t < fromT) return false;
        if (toT !== null && t > toT) return false;
      }
      return true;
    });
  }, [allLines, selectedBrand, dateFrom, dateTo]);

  const totalQuantity = useMemo(
    () => lines.reduce((sum, l) => sum + l.quantity, 0),
    [lines],
  );
  const totalAmount = useMemo(() => lines.reduce((sum, l) => sum + l.total, 0), [lines]);
  const totalCourier = useMemo(
    () => lines.reduce((sum, l) => sum + l.courierFee, 0),
    [lines],
  );
  const missingSettlementCount = useMemo(
    () => lines.filter((l) => !l.hasSettlementInfo).length,
    [lines],
  );

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-bold">정산 관리</h2>
        <p className="mt-1 text-sm text-gray-500">
          배송완료된 건의 정산 금액을 확인하세요. 도매가에 소량 추가단가·택배비가 더해진
          금액입니다.
        </p>
      </div>

      {/* 정산정보 미설정 안내 */}
      {missingSettlementCount > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          정산정보(추가단가·택배비)가 설정되지 않은 공급사 건이 {missingSettlementCount}건
          있습니다. 해당 건은 도매가만 합산되며, 관리자에게 정산정보 설정을 요청하세요.
        </div>
      )}

      {/* 기간 필터 — 배송완료일 기준 */}
      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-gray-500">기간(배송완료일)</span>
        <input
          type="date"
          value={dateFrom}
          max={dateTo || undefined}
          onChange={(e) => setDateFrom(e.target.value)}
          className="rounded border border-gray-300 px-2 py-1"
        />
        <span className="text-gray-400">~</span>
        <input
          type="date"
          value={dateTo}
          min={dateFrom || undefined}
          onChange={(e) => setDateTo(e.target.value)}
          className="rounded border border-gray-300 px-2 py-1"
        />
        <button
          onClick={() => {
            const now = new Date();
            setDateFrom(toYmd(new Date(now.getFullYear(), now.getMonth(), 1)));
            setDateTo(toYmd(new Date(now.getFullYear(), now.getMonth() + 1, 0)));
          }}
          className="rounded-full border border-gray-300 bg-white px-3 py-1 text-gray-600 hover:bg-gray-50"
        >
          이번달
        </button>
        <button
          onClick={() => {
            const now = new Date();
            setDateFrom(toYmd(new Date(now.getFullYear(), now.getMonth() - 1, 1)));
            setDateTo(toYmd(new Date(now.getFullYear(), now.getMonth(), 0)));
          }}
          className="rounded-full border border-gray-300 bg-white px-3 py-1 text-gray-600 hover:bg-gray-50"
        >
          지난달
        </button>
        {(dateFrom || dateTo) && (
          <button
            onClick={() => {
              setDateFrom('');
              setDateTo('');
            }}
            className="rounded-full border border-gray-300 bg-white px-3 py-1 text-gray-500 hover:bg-gray-50"
          >
            전체기간 ✕
          </button>
        )}
      </div>

      {/* 업체(공급사) 필터 — 정산금을 받는 업체별로 조회 */}
      {brandOptions.length > 1 && (
        <div className="mb-4 flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedBrand('all')}
            className={`rounded-full border px-3 py-1 text-sm ${
              selectedBrand === 'all'
                ? 'border-indigo-600 bg-indigo-600 text-white'
                : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            전체
          </button>
          {brandOptions.map((b) => (
            <button
              key={b.id}
              onClick={() => setSelectedBrand(b.id)}
              className={`rounded-full border px-3 py-1 text-sm ${
                selectedBrand === b.id
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {b.name}
            </button>
          ))}
        </div>
      )}

      {/* 요약 */}
      <div className="mb-4 grid grid-cols-3 gap-3">
        <div className="rounded-lg bg-white p-4 text-center shadow">
          <p className="text-2xl font-bold text-green-700">{lines.length}</p>
          <p className="text-sm text-gray-500">배송완료 건수</p>
        </div>
        <div className="rounded-lg bg-white p-4 text-center shadow">
          <p className="text-2xl font-bold text-indigo-700">{totalQuantity}</p>
          <p className="text-sm text-gray-500">총 수량</p>
        </div>
        <div className="rounded-lg bg-white p-4 text-center shadow">
          <p className="text-2xl font-bold text-rose-700">
            {totalAmount.toLocaleString()}
          </p>
          <p className="text-sm text-gray-500">총 정산금액 (원)</p>
        </div>
      </div>

      {loading ? (
        <div className="center-box py-16">
          <Loading />
        </div>
      ) : lines.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
          정산할 배송완료 건이 없습니다.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500">
              <tr>
                {isAdminView && <th className="px-3 py-2 text-left">업체</th>}
                <th className="px-3 py-2 text-left">상품</th>
                <th className="px-3 py-2 text-left">배송요청일</th>
                <th className="px-3 py-2 text-left">배송완료일</th>
                <th className="px-3 py-2 text-left">택배사</th>
                <th className="px-3 py-2 text-left">송장번호</th>
                <th className="px-3 py-2 text-right">도매단가(+소량)</th>
                <th className="px-3 py-2 text-right">× 수량</th>
                <th className="px-3 py-2 text-right">+ 택배비</th>
                <th className="px-3 py-2 text-right">= 정산금액</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {lines.map((l) => {
                const d = l.delivery;
                return (
                  <tr key={d._id} className="hover:bg-gray-50">
                    {/* 업체 (admin 전용) */}
                    {isAdminView && (
                      <td className="whitespace-nowrap px-3 py-3 text-xs font-medium text-gray-700">
                        {typeof d.giftItemBrand === 'object' && d.giftItemBrand?.name ? (
                          d.giftItemBrand.name
                        ) : (
                          <span className="text-gray-300">빈업체</span>
                        )}
                      </td>
                    )}
                    {/* 상품 */}
                    <td className="px-3 py-3">
                      <p className="max-w-[160px] truncate font-medium">
                        {getItemName(d)}
                      </p>
                      <p className="text-[11px] text-gray-400">
                        {d.recipient?.name || '-'}
                      </p>
                    </td>
                    {/* 배송요청일 */}
                    <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                      {formatDate(d.recipient?.submittedAt)}
                    </td>
                    {/* 배송완료일 */}
                    <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                      {formatDate(d.shipping?.deliveredAt)}
                    </td>
                    {/* 택배사 */}
                    <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-600">
                      {d.shipping?.carrier || '-'}
                    </td>
                    {/* 송장번호 */}
                    <td className="whitespace-nowrap px-3 py-3 font-mono text-xs text-gray-600">
                      {d.shipping?.trackingNumber || '-'}
                    </td>
                    {/* 도매단가(+소량) */}
                    <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                      {l.wholePrice.toLocaleString()}
                      {l.extraUnitPrice > 0 && (
                        <span className="text-amber-600">
                          (+{l.extraUnitPrice.toLocaleString()})
                        </span>
                      )}
                    </td>
                    {/* × 수량 */}
                    <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                      {l.quantity}개
                    </td>
                    {/* + 택배비 */}
                    <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                      {l.courierFee > 0 ? (
                        l.courierFee.toLocaleString()
                      ) : l.courierFreeByQuantity ? (
                        <span className="text-[11px] text-gray-400">
                          무료({l.courierFreeMinQuantity}충족)
                        </span>
                      ) : l.courierBundled ? (
                        <span className="text-[11px] text-gray-400">묶음면제</span>
                      ) : l.courierFeeBase > 0 ? (
                        '0'
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    {/* = 정산금액 */}
                    <td className="whitespace-nowrap px-3 py-3 text-right">
                      <span className="font-semibold text-gray-800 tabular-nums">
                        {formatWon(l.total)}
                      </span>
                      {!l.hasSettlementInfo && (
                        <span className="block text-[10px] text-amber-600">
                          정산정보 미설정
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 text-sm font-semibold">
              <tr>
                <td className="px-3 py-3 text-gray-500" colSpan={isAdminView ? 7 : 6}>
                  합계 ({lines.length}건)
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-500">
                  {totalQuantity}개
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-500">
                  {totalCourier > 0 ? totalCourier.toLocaleString() : '-'}
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-rose-700">
                  {formatWon(totalAmount)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <button
          disabled
          title="정산 확정 기능은 백엔드 준비 후 사용할 수 있습니다."
          className="cursor-not-allowed rounded-lg bg-gray-300 px-5 py-2 text-sm font-medium text-white"
        >
          정산 확정 (준비중)
        </button>
      </div>
    </div>
  );
};
